/**
 * Display helpers for Rheya chat lists.
 *
 * Text is always shown when present. Voice is parallel presentation
 * (playingKey highlight), not a gate on bubble visibility.
 */

export type ChatListItem =
  | { role: 'user' | 'assistant'; content: string; messageId?: string }
  | { role: 'status'; toolUse: { name: string }; messageId?: string }

export type ChatListRow<T> = {
  item: T
  /** Index in the full store message array (stable for audio keys). */
  index: number
}

/**
 * Prefer server messageId; fall back to index+content for legacy rows.
 * Used by auto-speak policy and bubble play controls.
 */
export function assistantMessageKey(index: number, content: string, messageId?: string): string {
  if (messageId) return messageId
  return `${index}:${content.trim().slice(0, 64)}`
}

/**
 * Rows for lists: drop blank lines only. Does not hide text for voice.
 * `heldKeys` is accepted for API compatibility and ignored.
 */
export function toVoiceGatedRows<T extends ChatListItem>(
  messages: readonly T[],
  _heldKeys?: ReadonlySet<string>,
): ChatListRow<T>[] {
  const rows: ChatListRow<T>[] = []
  for (let index = 0; index < messages.length; index++) {
    const item = messages[index]!
    if (item.role === 'assistant') {
      const body = item.content?.trim() ?? ''
      if (!body) continue
    }
    if (item.role === 'user') {
      const body = item.content?.trim() ?? ''
      if (!body) continue
    }
    if (item.role === 'status') {
      const name = item.toolUse?.name?.trim() ?? ''
      if (!name) continue
    }
    rows.push({ item, index })
  }
  return rows
}

export type AgentStatus =
  | { type: 'idle' }
  | { type: 'typing' }
  | { type: 'toolUse'; toolUse: { name: string } }

/** Footer / status copy for the chat list. */
export function rheyaStatusLabel(status: AgentStatus, _heldKeyCount?: number): string | null {
  if (status.type === 'typing') return 'Rheya is typing…'
  if (status.type === 'toolUse') return `Working: ${status.toolUse.name}`
  return null
}
